import { NpiLookup, PracticeLocation } from "../npiLookup";

import { SignupForm } from "../signupForm";
import { US_STATES } from "@verse_core/shared_components/usStates";
import { validateFaxOrPhone } from "../../util/stringUtil";

/** The states sign-up accepts: the 50 states and DC, each of which the backend
 * gives a timezone. No territories. */
export const SIGNUP_STATES = Object.keys(US_STATES);

export type AccountInfoField =
  | "firstName"
  | "lastName"
  | "phone"
  | "facilityName"
  | "facilityTypeId"
  | "address1"
  | "city"
  | "stateAbbr"
  | "zipcode"
  | "facilityPhone"
  | "facilityFax"
  | "secondaryFacilityFax"
  | "medicalRecordsFax";

const PHONE_ERROR = "Please enter a valid 10-digit phone number";
const FAX_ERROR = "Please enter a valid 10-digit fax number";

/** An optional fax is valid when blank or a US number. */
const faxError = (fax: string) =>
  fax.trim() && !validateFaxOrPhone(fax) ? FAX_ERROR : null;

/** The facility fields a sign-up and a partner's new referring facility share. */
export type FacilityInfoForm = Pick<
  SignupForm,
  | "facilityName"
  | "facilityTypeId"
  | "address1"
  | "address2"
  | "city"
  | "stateAbbr"
  | "zipcode"
  | "facilityPhone"
  | "facilityFax"
  | "secondaryFacilityFax"
>;

type FacilityInfoField = Exclude<keyof FacilityInfoForm, "address2">;

/** Keep only the fields that have an error. */
const presentErrors = <Field extends string>(
  errors: Partial<Record<Field, string | null>>,
) =>
  Object.fromEntries(
    Object.entries(errors).filter(([, error]) => error),
  ) as Partial<Record<Field, string>>;

/** The error for each invalid facility field, matching what the backend's
 * SignupFacility accepts. */
export function facilityInfoErrors(
  form: FacilityInfoForm,
): Partial<Record<FacilityInfoField, string>> {
  return presentErrors<FacilityInfoField>({
    facilityName: form.facilityName.trim()
      ? null
      : "Please enter the facility name",
    facilityTypeId:
      form.facilityTypeId !== null ? null : "Please select a facility type",
    address1: form.address1.trim() ? null : "Please enter the facility address",
    city: form.city.trim() ? null : "Please enter the city",
    stateAbbr: SIGNUP_STATES.includes(form.stateAbbr)
      ? null
      : "Please select a state",
    zipcode: form.zipcode.trim() ? null : "Please enter the ZIP code",
    facilityPhone: validateFaxOrPhone(form.facilityPhone) ? null : PHONE_ERROR,
    facilityFax: form.facilityFax.trim()
      ? faxError(form.facilityFax)
      : "Please enter the facility fax number",
    secondaryFacilityFax: faxError(form.secondaryFacilityFax),
  });
}

/** The error for each invalid account info field, matching what the sign-up
 * backend accepts. Empty when the step can continue. */
export function accountInfoErrors(
  form: SignupForm,
): Partial<Record<AccountInfoField, string>> {
  return {
    ...presentErrors<AccountInfoField>({
      firstName: form.firstName.trim() ? null : "Please enter your first name",
      lastName: form.lastName.trim() ? null : "Please enter your last name",
      phone: validateFaxOrPhone(form.phone) ? null : PHONE_ERROR,
      medicalRecordsFax: faxError(form.medicalRecordsFax),
    }),
    ...facilityInfoErrors(form),
  };
}

/** Which sections the lookup filled in: the name, and the facility if NPPES
 * lists any location. */
export const prefilledSections = (lookup: NpiLookup | null) => ({
  personal: !!(lookup?.first_name && lookup.last_name),
  facility: (lookup?.practice_locations.length ?? 0) > 0,
});

/** The facility's address and numbers from a practice location, or empty. */
export const locationFields = (location: PracticeLocation | null) => ({
  address1: location?.address1 ?? "",
  address2: location?.address2 ?? "",
  city: location?.city ?? "",
  stateAbbr: location?.state ?? "",
  zipcode: location?.zip ?? "",
  facilityPhone: location?.phone ?? "",
  facilityFax: location?.fax ?? "",
});

/** Step 1 after a new lookup: the name, the location's address and numbers if
 * there's exactly one, and everything else cleared for the new provider. */
export const prefillFromLookup = (lookup: NpiLookup): Partial<SignupForm> => ({
  firstName: lookup.first_name,
  lastName: lookup.last_name,
  phone: "",
  facilityName: "",
  facilityTypeId: null,
  secondaryFacilityFax: "",
  medicalRecordsFax: "",
  ...locationFields(
    lookup.practice_locations.length === 1
      ? lookup.practice_locations[0]
      : null,
  ),
});

/** An address on one line, e.g. "158 W 27 ST, NEW YORK, NY 10001". */
export const formatAddress = (
  address: Pick<
    SignupForm,
    "address1" | "address2" | "city" | "stateAbbr" | "zipcode"
  >,
) =>
  [
    address.address1,
    address.address2,
    address.city,
    `${address.stateAbbr} ${address.zipcode}`,
  ]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(", ");
